import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, ChevronRight, Lock, CheckCircle2, Clock, FileText, ListChecks, TrendingUp, AlertCircle, MessageSquare, Bold, Italic, List, Eye, Pencil, Paperclip, X, Download } from "lucide-react";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import axios from "axios";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const fmtPeriod = (y, m) => `${MONTHS[m - 1]} ${y}`;

const STATUS_META = {
  draft: { label: "Draft", classes: "bg-slate-100 text-slate-700 border-slate-200", icon: FileText },
  submitted: { label: "Awaiting sign-off", classes: "bg-amber-100 text-amber-800 border-amber-200", icon: Clock },
  signed_off: { label: "Signed off", classes: "bg-emerald-100 text-emerald-800 border-emerald-200", icon: CheckCircle2 },
};

const StatusBadge = ({ status }) => {
  const meta = STATUS_META[status] || { label: status, classes: "bg-slate-100 text-slate-700 border-slate-200", icon: FileText };
  const Icon = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-mono border ${meta.classes}`}>
      <Icon className="h-3 w-3" />
      {meta.label}
    </span>
  );
};

export default function People() {
  const { user } = useAuth();
  const [people, setPeople] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);

  const loadPeople = async () => {
    setLoading(true);
    try {
      const r = await api.get("/people/visible");
      setPeople(r.data);
      // Default selection: me
      const me = r.data.find((p) => p.is_me) || r.data[0];
      if (me && !selectedId) setSelectedId(me.user_id);
    } finally {
      setLoading(false);
    }
  };

  const loadReviews = async (uid) => {
    if (!uid) return;
    setReviewsLoading(true);
    try {
      const r = await api.get(`/people/${uid}/reviews`);
      setReviews(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load reviews");
      setReviews([]);
    } finally {
      setReviewsLoading(false);
    }
  };

  useEffect(() => { loadPeople(); }, []);
  useEffect(() => { if (selectedId) loadReviews(selectedId); }, [selectedId]);

  const selected = useMemo(() => people.find((p) => p.user_id === selectedId), [people, selectedId]);
  const isOwn = selected?.is_me;
  const isMyReport = selected?.is_my_report;
  const isAdmin = user?.role === "admin";
  const seesEveryone = !!user?.people_sees_all;

  return (
    <div className="space-y-6" data-testid="people-page">
      <div>
        <div className="text-xs font-mono uppercase tracking-[0.2em] text-blue-700">Team</div>
        <h1 className="font-heading text-3xl font-bold text-slate-900 mt-1">People reviews</h1>
        <p className="text-slate-500 text-sm mt-1 max-w-2xl">
          Monthly reflection: top accomplishments, priorities for next period, what's worked and what hasn't. Submit when ready and your manager will sign it off.
        </p>
      </div>

      <div className="grid lg:grid-cols-[280px_1fr] gap-6">
        {/* People list */}
        <Card className="border-slate-200 h-fit">
          <CardHeader className="border-b border-slate-100">
            <CardTitle className="text-sm font-heading">
              {seesEveryone ? "Everyone" : "Your team"}
              <span className="text-slate-400 font-mono text-xs ml-2">({people.length})</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-4 text-sm text-slate-400">Loading…</div>
            ) : (
              <ul className="divide-y divide-slate-100" data-testid="people-list">
                {people.map((p) => {
                  const active = p.user_id === selectedId;
                  const latest = p.latest_review;
                  return (
                    <li key={p.user_id}>
                      <button
                        type="button"
                        onClick={() => setSelectedId(p.user_id)}
                        className={`w-full text-left px-4 py-3 transition-colors ${active ? "bg-blue-50" : "hover:bg-slate-50"}`}
                        data-testid={`people-row-${p.user_id}`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div className="min-w-0">
                            <div className="font-medium text-sm text-slate-900 truncate flex items-center gap-1.5">
                              {p.name}
                              {p.is_me && <span className="text-[9px] font-mono bg-blue-100 text-blue-800 px-1 rounded">YOU</span>}
                            </div>
                            <div className="text-[11px] text-slate-500 truncate">{p.job_title || p.role}</div>
                          </div>
                          <ChevronRight className={`h-4 w-4 flex-shrink-0 ${active ? "text-blue-700" : "text-slate-300"}`} />
                        </div>
                        {latest ? (
                          <div className="mt-1.5 flex items-center gap-1.5 text-[10px]">
                            <span className="text-slate-400 font-mono">{fmtPeriod(latest.period_year, latest.period_month)}</span>
                            <StatusBadge status={latest.status} />
                          </div>
                        ) : (
                          <div className="mt-1.5 text-[10px] text-slate-400 italic">No reviews yet</div>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </CardContent>
        </Card>

        {/* Reviews for selected person */}
        <div className="space-y-4 min-w-0">
          {selected && (
            <Card className="border-slate-200">
              <CardContent className="p-5 flex flex-wrap items-center gap-4 justify-between">
                <div>
                  <div className="font-heading text-xl font-bold text-slate-900">{selected.name}</div>
                  <div className="text-sm text-slate-500">{selected.job_title || selected.role} · {selected.email}</div>
                </div>
                {isOwn && (
                  <Button onClick={() => setShowCreate(true)} data-testid="new-review-btn">
                    <Plus className="h-4 w-4 mr-1.5" /> Start review
                  </Button>
                )}
              </CardContent>
            </Card>
          )}

          {reviewsLoading ? (
            <div className="text-sm text-slate-400 font-mono">Loading reviews…</div>
          ) : reviews.length === 0 ? (
            <Card className="border-slate-200">
              <CardContent className="p-10 text-center">
                <FileText className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                <div className="font-heading text-base text-slate-700">No reviews yet</div>
                <p className="text-sm text-slate-500 mt-1 max-w-sm mx-auto">
                  {isOwn
                    ? "Start your first monthly review to capture what you accomplished and where you're heading."
                    : isMyReport
                      ? "Once they submit a review you'll be able to sign it off here."
                      : "Reviews will appear here once written."}
                </p>
              </CardContent>
            </Card>
          ) : (
            <ul className="space-y-3" data-testid="reviews-list">
              {reviews.map((r) => (
                <ReviewCard
                  key={r.id}
                  review={r}
                  isOwn={isOwn}
                  isMyReport={isMyReport}
                  isAdmin={isAdmin}
                  onEdit={() => setEditing(r)}
                  onAfter={() => loadReviews(selectedId).then(() => loadPeople())}
                />
              ))}
            </ul>
          )}
        </div>
      </div>

      {showCreate && selected && (
        <CreateReviewDialog
          person={selected}
          existing={reviews}
          onClose={() => setShowCreate(false)}
          onCreated={(r) => {
            setShowCreate(false);
            setEditing(r);
            loadReviews(selectedId).then(() => loadPeople());
          }}
        />
      )}

      {editing && (
        <EditReviewDialog
          review={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            loadReviews(selectedId).then(() => loadPeople());
          }}
        />
      )}
    </div>
  );
}

/* ------- Review card ------- */
function ReviewCard({ review, isOwn, isMyReport, isAdmin, onEdit, onAfter }) {
  const [busy, setBusy] = useState(false);

  const signOff = async () => {
    if (!window.confirm(`Sign off ${fmtPeriod(review.period_year, review.period_month)} review? Once signed off it is locked.`)) return;
    setBusy(true);
    try {
      await api.post(`/people/reviews/${review.id}/sign-off`);
      toast.success("Signed off");
      onAfter && onAfter();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sign-off failed");
    } finally {
      setBusy(false);
    }
  };

  const canEdit = isOwn && review.status === "draft";
  const canSignOff = (isMyReport || isAdmin) && review.status === "submitted";

  return (
    <li>
      <Card className="border-slate-200">
        <CardContent className="p-5 space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-wider text-blue-700">{fmtPeriod(review.period_year, review.period_month)}</div>
              <div className="text-xs text-slate-500 mt-0.5">
                Last updated {new Date(review.updated_at).toLocaleString("en-GB")}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <StatusBadge status={review.status} />
              {canEdit && <Button size="sm" variant="outline" onClick={onEdit} data-testid={`edit-review-${review.id}`}>Edit</Button>}
              {canSignOff && (
                <Button size="sm" onClick={signOff} disabled={busy} data-testid={`signoff-${review.id}`}>
                  {busy ? "Signing…" : "Sign off"}
                </Button>
              )}
            </div>
          </div>

          {review.status === "signed_off" && (
            <div className="text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-100 rounded px-2 py-1 font-mono inline-flex items-center gap-1.5">
              <Lock className="h-3 w-3" />
              Signed off by {review.signed_off_by_name || "—"} on {review.signed_off_at ? new Date(review.signed_off_at).toLocaleDateString("en-GB") : "—"}
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-4">
            <ReviewSection icon={ListChecks} title={`Top accomplishments (${review.accomplishments?.length || 0}/5)`}>
              {review.accomplishments?.length ? (
                <ol className="list-decimal list-inside space-y-1 text-sm text-slate-700">
                  {review.accomplishments.map((a) => <li key={a}>{a}</li>)}
                </ol>
              ) : (
                <Empty />
              )}
            </ReviewSection>
            <ReviewSection icon={TrendingUp} title={`Priorities next period (${review.priorities?.length || 0}/3)`}>
              {review.priorities?.length ? (
                <ol className="list-decimal list-inside space-y-1 text-sm text-slate-700">
                  {review.priorities.map((a) => <li key={a}>{a}</li>)}
                </ol>
              ) : (
                <Empty />
              )}
            </ReviewSection>
            <ReviewSection icon={CheckCircle2} title="What worked">
              <Para text={review.worked} />
            </ReviewSection>
            <ReviewSection icon={AlertCircle} title="What didn't">
              <Para text={review.not_worked} />
            </ReviewSection>
          </div>

          <ReviewSection icon={MessageSquare} title="Summary">
            <Para text={review.summary} />
          </ReviewSection>

          {(review.attachments || []).length > 0 && (
            <ReviewSection icon={Paperclip} title="Attachments">
              <div className="flex flex-wrap gap-1.5">
                {review.attachments.map((a) => (
                  <a
                    key={a.id}
                    href="#"
                    onClick={async (e) => {
                      e.preventDefault();
                      try {
                        const r = await api({ url: `/people/reviews/attachments/${a.id}`, method: "GET", responseType: "blob" });
                        const url = URL.createObjectURL(r.data);
                        const link = document.createElement("a");
                        link.href = url; link.download = a.filename; link.click();
                        URL.revokeObjectURL(url);
                      } catch { toast.error("Download failed"); }
                    }}
                    className="inline-flex items-center gap-1.5 text-xs bg-slate-50 border border-slate-200 rounded-md px-2 py-1 hover:bg-slate-100"
                    data-testid={`review-att-${a.id}`}
                  >
                    <Paperclip className="h-3 w-3 text-slate-400" />
                    <span>{a.filename}</span>
                    <span className="text-slate-400">· {Math.max(1, Math.round((a.size || 0) / 1024))} KB</span>
                  </a>
                ))}
              </div>
            </ReviewSection>
          )}
        </CardContent>
      </Card>
    </li>
  );
}

const Empty = () => <div className="text-xs text-slate-400 italic">— Not filled in —</div>;
const Para = ({ text }) => text ? (
  <div className="prose prose-sm max-w-none text-slate-700"><ReactMarkdown>{text}</ReactMarkdown></div>
) : <Empty />;

const ReviewSection = ({ icon: Icon, title, children }) => (
  <div>
    <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500 flex items-center gap-1.5 mb-1.5">
      <Icon className="h-3 w-3" />
      {title}
    </div>
    <div className="text-sm">{children}</div>
  </div>
);

/* ------- Create dialog ------- */
function CreateReviewDialog({ person, existing, onClose, onCreated }) {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [busy, setBusy] = useState(false);

  const existingPeriods = new Set(existing.map((r) => `${r.period_year}-${r.period_month}`));
  const conflict = existingPeriods.has(`${year}-${month}`);

  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/people/${person.user_id}/reviews`, { period_year: Number(year), period_month: Number(month) });
      onCreated(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create review");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Start a review</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <label className="text-[10px] font-mono uppercase tracking-wider text-slate-500">Period</label>
            <div className="grid grid-cols-2 gap-2 mt-1">
              <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
                <SelectTrigger data-testid="review-month-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MONTHS.map((m, idx) => <SelectItem key={m} value={String(idx + 1)}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
              <Input type="number" value={year} min={2024} max={2100} onChange={(e) => setYear(Number(e.target.value))} data-testid="review-year-input" />
            </div>
          </div>
          {conflict && (
            <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2">
              A review for {fmtPeriod(year, month)} already exists. Pick a different month.
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy || conflict} data-testid="review-create-confirm">
            {busy ? "Creating…" : `Create ${fmtPeriod(year, month)}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ------- Rich text area with markdown toolbar + preview toggle ------- */
function RichTextArea({ value, onChange, placeholder, rows = 5, className = "", testid, tintClass = "" }) {
  const [mode, setMode] = useState("write"); // 'write' | 'preview'
  const ref = React.useRef(null);
  const wrapSelection = (before, after = before) => {
    const ta = ref.current;
    if (!ta) return;
    const start = ta.selectionStart, end = ta.selectionEnd;
    const sel = value.slice(start, end);
    const next = value.slice(0, start) + before + (sel || "") + after + value.slice(end);
    onChange(next);
    setTimeout(() => {
      ta.focus();
      ta.setSelectionRange(start + before.length, start + before.length + (sel?.length || 0));
    }, 0);
  };
  const prefixLine = (prefix) => {
    const ta = ref.current;
    if (!ta) return;
    const start = ta.selectionStart, end = ta.selectionEnd;
    const lineStart = value.lastIndexOf("\n", start - 1) + 1;
    const next = value.slice(0, lineStart) + prefix + value.slice(lineStart);
    onChange(next);
    setTimeout(() => { ta.focus(); ta.setSelectionRange(end + prefix.length, end + prefix.length); }, 0);
  };
  return (
    <div className={`rounded-md border border-slate-200 ${className}`}>
      <div className="flex items-center gap-1 border-b border-slate-100 bg-slate-50/60 px-2 py-1">
        <button type="button" onClick={() => wrapSelection("**")} className="p-1 rounded hover:bg-slate-200 text-slate-600" title="Bold (**)" data-testid={testid ? `${testid}-bold` : undefined}><Bold className="h-3.5 w-3.5" /></button>
        <button type="button" onClick={() => wrapSelection("*")} className="p-1 rounded hover:bg-slate-200 text-slate-600" title="Italic (*)" data-testid={testid ? `${testid}-italic` : undefined}><Italic className="h-3.5 w-3.5" /></button>
        <button type="button" onClick={() => prefixLine("- ")} className="p-1 rounded hover:bg-slate-200 text-slate-600" title="Bullet list" data-testid={testid ? `${testid}-list` : undefined}><List className="h-3.5 w-3.5" /></button>
        <div className="flex-1"></div>
        <button type="button" onClick={() => setMode(mode === "write" ? "preview" : "write")} className={`p-1 rounded hover:bg-slate-200 text-slate-600 flex items-center gap-1 text-[11px] font-medium ${mode === "preview" ? "bg-slate-200" : ""}`} data-testid={testid ? `${testid}-toggle-preview` : undefined}>
          {mode === "write" ? <><Eye className="h-3.5 w-3.5" /> Preview</> : <><Pencil className="h-3.5 w-3.5" /> Edit</>}
        </button>
      </div>
      {mode === "write" ? (
        <Textarea
          ref={ref} rows={rows} value={value} onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className={`resize-none border-0 focus-visible:ring-0 ${tintClass}`}
          data-testid={testid}
        />
      ) : (
        <div className={`prose prose-sm max-w-none p-3 min-h-[100px] ${tintClass}`} data-testid={testid ? `${testid}-preview` : undefined}>
          {value ? <ReactMarkdown>{value}</ReactMarkdown> : <span className="text-slate-400 italic">Nothing to preview yet.</span>}
        </div>
      )}
    </div>
  );
}

/* ------- Review attachments ------- */
function ReviewAttachments({ review, canEdit, onChanged }) {
  const items = review.attachments || [];
  const [uploading, setUploading] = useState(false);
  const inputRef = React.useRef(null);

  const upload = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        await api.post(`/people/reviews/${review.id}/attachments`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      toast.success(`Uploaded ${files.length} file${files.length === 1 ? "" : "s"}`);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };
  const remove = async (attId) => {
    if (!window.confirm("Delete this attachment?")) return;
    try {
      await api.delete(`/people/reviews/${review.id}/attachments/${attId}`);
      toast.success("Deleted");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };
  const download = async (att) => {
    // Use axios to avoid opening a broken tab if the request fails
    try {
      const r = await api({ url: `/people/reviews/attachments/${att.id}`, method: "GET", responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = att.filename; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Download failed");
    }
  };

  return (
    <section className="bg-white rounded-lg border border-slate-200 p-4" data-testid="review-attachments">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-full bg-teal-100 text-teal-700 flex items-center justify-center">
            <Paperclip className="h-4 w-4" />
          </div>
          <div>
            <div className="font-heading text-sm font-semibold text-slate-900">Attachments</div>
            <div className="text-[11px] text-slate-500">Screenshots, docs or evidence to back up your review.</div>
          </div>
        </div>
        {canEdit && (
          <label className="inline-flex items-center gap-1.5 border border-slate-300 rounded-md px-2.5 py-1 text-xs cursor-pointer hover:bg-slate-50" data-testid="upload-attachment-btn">
            {uploading ? "Uploading…" : <><Paperclip className="h-3.5 w-3.5" /> Add file</>}
            <input ref={inputRef} type="file" multiple className="hidden" onChange={(e) => upload(e.target.files)} disabled={uploading} />
          </label>
        )}
      </div>
      {items.length === 0 ? (
        <div className="text-xs text-slate-400 italic py-2">No attachments yet.</div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-2">
          {items.map((a) => (
            <div key={a.id} className="flex items-center gap-2 border border-slate-200 rounded-md px-2.5 py-1.5 bg-slate-50/50" data-testid={`attachment-${a.id}`}>
              <FileText className="h-4 w-4 text-slate-400 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-xs text-slate-800 truncate">{a.filename}</div>
                <div className="text-[10px] text-slate-400">{Math.max(1, Math.round((a.size || 0) / 1024))} KB · by {a.uploaded_by}</div>
              </div>
              <button className="p-1 rounded hover:bg-slate-200 text-slate-500" onClick={() => download(a)} title="Download" data-testid={`att-download-${a.id}`}><Download className="h-3.5 w-3.5" /></button>
              {canEdit && <button className="p-1 rounded hover:bg-red-100 text-red-500" onClick={() => remove(a.id)} title="Delete" data-testid={`att-delete-${a.id}`}><X className="h-3.5 w-3.5" /></button>}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

/* ------- Edit dialog ------- */
function EditReviewDialog({ review, onClose, onSaved }) {
  const [accomplishments, setAccomplishments] = useState([...(review.accomplishments || []), "", "", "", "", ""].slice(0, 5));
  const [priorities, setPriorities] = useState([...(review.priorities || []), "", "", ""].slice(0, 3));
  const [worked, setWorked] = useState(review.worked || "");
  const [notWorked, setNotWorked] = useState(review.not_worked || "");
  const [summary, setSummary] = useState(review.summary || "");
  const [busy, setBusy] = useState(false);

  const save = async (alsoSubmit) => {
    setBusy(true);
    try {
      const cleaned = {
        accomplishments: accomplishments.map((s) => s.trim()).filter(Boolean),
        priorities: priorities.map((s) => s.trim()).filter(Boolean),
        worked, not_worked: notWorked, summary,
      };
      await api.patch(`/people/reviews/${review.id}`, cleaned);
      if (alsoSubmit) {
        await api.post(`/people/reviews/${review.id}/submit`);
        toast.success("Submitted for sign-off");
      } else {
        toast.success("Draft saved");
      }
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const setAcc = (i, v) => setAccomplishments((p) => p.map((x, idx) => idx === i ? v : x));
  const setPri = (i, v) => setPriorities((p) => p.map((x, idx) => idx === i ? v : x));

  const accFilled = accomplishments.filter((s) => s.trim()).length;
  const priFilled = priorities.filter((s) => s.trim()).length;
  const wordsWorked = worked.trim().split(/\s+/).filter(Boolean).length;
  const wordsNotWorked = notWorked.trim().split(/\s+/).filter(Boolean).length;
  const wordsSummary = summary.trim().split(/\s+/).filter(Boolean).length;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] p-0 gap-0 overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 bg-gradient-to-r from-indigo-50 via-blue-50 to-white border-b border-slate-200">
          <div className="text-[10px] font-mono uppercase tracking-wider text-indigo-600">Review period</div>
          <div className="font-heading text-xl font-bold text-slate-900 mt-0.5">{fmtPeriod(review.period_year, review.period_month)}</div>
          <div className="text-xs text-slate-500 mt-1">Fill this in honestly — your manager sees only what you save.</div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6 bg-slate-50/40">
          {/* Accomplishments */}
          <section className="bg-white rounded-lg border border-slate-200 p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center">
                  <ListChecks className="h-4 w-4" />
                </div>
                <div>
                  <div className="font-heading text-sm font-semibold text-slate-900">Top 5 accomplishments</div>
                  <div className="text-[11px] text-slate-500">What are you most proud of this period?</div>
                </div>
              </div>
              <span className="text-xs font-mono text-slate-400">{accFilled}/5</span>
            </div>
            <div className="space-y-2">
              {accomplishments.map((v, i) => (
                <div key={i} className="flex items-center gap-2 group">
                  <div className={`h-6 w-6 flex-shrink-0 rounded-full flex items-center justify-center text-[11px] font-semibold ${v.trim() ? "bg-blue-600 text-white" : "bg-slate-200 text-slate-500 group-focus-within:bg-blue-100 group-focus-within:text-blue-700"}`}>{i + 1}</div>
                  <Input
                    value={v} onChange={(e) => setAcc(i, e.target.value)}
                    placeholder={i === 0 ? "e.g. Closed Acme renewal at £28k MRR" : "Add another win…"}
                    className={`flex-1 ${v.trim() ? "" : "bg-slate-50/60"}`}
                    data-testid={`acc-input-${i}`} autoFocus={i === 0}
                  />
                </div>
              ))}
            </div>
          </section>

          {/* Priorities */}
          <section className="bg-white rounded-lg border border-slate-200 p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-full bg-violet-100 text-violet-700 flex items-center justify-center">
                  <TrendingUp className="h-4 w-4" />
                </div>
                <div>
                  <div className="font-heading text-sm font-semibold text-slate-900">Top 3 priorities for next period</div>
                  <div className="text-[11px] text-slate-500">Where should your focus be next?</div>
                </div>
              </div>
              <span className="text-xs font-mono text-slate-400">{priFilled}/3</span>
            </div>
            <div className="space-y-2">
              {priorities.map((v, i) => (
                <div key={i} className="flex items-center gap-2 group">
                  <div className={`h-6 w-6 flex-shrink-0 rounded-full flex items-center justify-center text-[11px] font-semibold ${v.trim() ? "bg-violet-600 text-white" : "bg-slate-200 text-slate-500 group-focus-within:bg-violet-100 group-focus-within:text-violet-700"}`}>{i + 1}</div>
                  <Input
                    value={v} onChange={(e) => setPri(i, e.target.value)}
                    placeholder={i === 0 ? "e.g. Close 3 new logos in Q3" : "Add another priority…"}
                    className={`flex-1 ${v.trim() ? "" : "bg-slate-50/60"}`}
                    data-testid={`pri-input-${i}`}
                  />
                </div>
              ))}
            </div>
          </section>

          {/* What worked / didn't */}
          <div className="grid md:grid-cols-2 gap-4">
            <section className="bg-white rounded-lg border-l-4 border-l-emerald-500 border-y border-r border-slate-200 p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                  <div className="font-heading text-sm font-semibold text-slate-900">What worked well</div>
                </div>
                <span className="text-[10px] font-mono text-slate-400">{wordsWorked} word{wordsWorked === 1 ? "" : "s"}</span>
              </div>
              <Textarea rows={5} value={worked} onChange={(e) => setWorked(e.target.value)}
                        placeholder="Habits, processes or people that helped you succeed…"
                        className="resize-none bg-emerald-50/30 focus:bg-white hidden"
                        data-testid="worked-textarea" />
              <RichTextArea value={worked} onChange={setWorked}
                            placeholder="Habits, processes or people that helped you succeed…"
                            testid="worked-textarea-rich" tintClass="bg-emerald-50/30" />
            </section>
            <section className="bg-white rounded-lg border-l-4 border-l-amber-500 border-y border-r border-slate-200 p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 text-amber-600" />
                  <div className="font-heading text-sm font-semibold text-slate-900">What didn't</div>
                </div>
                <span className="text-[10px] font-mono text-slate-400">{wordsNotWorked} word{wordsNotWorked === 1 ? "" : "s"}</span>
              </div>
              <Textarea rows={5} value={notWorked} onChange={(e) => setNotWorked(e.target.value)}
                        placeholder="Blockers, gaps or things you'd do differently next time…"
                        className="resize-none bg-amber-50/30 focus:bg-white hidden"
                        data-testid="notworked-textarea" />
              <RichTextArea value={notWorked} onChange={setNotWorked}
                            placeholder="Blockers, gaps or things you'd do differently next time…"
                            testid="notworked-textarea-rich" tintClass="bg-amber-50/30" />
            </section>
          </div>

          {/* Summary */}
          <section className="bg-white rounded-lg border border-slate-200 p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-full bg-slate-100 text-slate-700 flex items-center justify-center">
                  <MessageSquare className="h-4 w-4" />
                </div>
                <div>
                  <div className="font-heading text-sm font-semibold text-slate-900">Overall summary</div>
                  <div className="text-[11px] text-slate-500">One paragraph — how the period went as a whole.</div>
                </div>
              </div>
              <span className="text-[10px] font-mono text-slate-400">{wordsSummary} word{wordsSummary === 1 ? "" : "s"}</span>
            </div>
            <Textarea rows={4} value={summary} onChange={(e) => setSummary(e.target.value)}
                      placeholder="Take a step back — what's the headline?"
                      className="resize-none hidden"
                      data-testid="summary-textarea" />
            <RichTextArea value={summary} onChange={setSummary}
                          placeholder="Take a step back — what's the headline?"
                          rows={4}
                          testid="summary-textarea-rich" />
          </section>

          <ReviewAttachments review={review} canEdit={review.status !== "signed_off"} onChanged={onSaved} />
        </div>

        {/* Footer */}
        <div className="border-t border-slate-200 bg-white px-6 py-3 flex flex-wrap items-center justify-between gap-2">
          <div className="text-[11px] text-slate-500">
            {accFilled === 0 && priFilled === 0 && !worked && !notWorked && !summary ? (
              "Nothing filled yet — start with a few accomplishments."
            ) : (
              <>Draft-saving keeps you moving. Submit only when you're ready for sign-off.</>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button variant="outline" onClick={() => save(false)} disabled={busy} data-testid="save-draft-btn">
              {busy ? "Saving…" : "Save draft"}
            </Button>
            <Button onClick={() => save(true)} disabled={busy} data-testid="submit-review-btn"
                    className="bg-blue-600 hover:bg-blue-700 text-white">
              {busy ? "Saving…" : "Save & submit for sign-off"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
